import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CRMUser } from '../types';
import type {
  BonusPlan,
  ObligationSettings,
  ObligationSlice,
  PayrollPosition,
  PayrollSettings,
  TaxPlan,
} from '../types/finances';
import {
  createDefaultBonusPlan,
  createDefaultTaxPlan,
  DEFAULT_OBLIGATION_SETTINGS,
  DEFAULT_PAYROLL_SETTINGS,
} from '../types/finances';
import { computeBonusPlan, type ComputedBonusPlan } from '../utils/bonusPlan';
import { computeTaxPlan, type ComputedTaxPlan, obligationYearsForHorizon } from '../utils/taxPlan';
import {
  saveBonusPlan,
  saveObligationSettings,
  saveTaxPlan,
  subscribeBonusPlan,
  subscribeObligationSettings,
  subscribeTaxPlan,
} from '../utils/obligationPlanService';

export { obligationYearsForHorizon };

export function useObligationWorkspace(args: {
  years: number[];
  positions: PayrollPosition[];
  payrollSettings?: PayrollSettings;
  crmUsers: CRMUser[];
  actorUserId?: string;
  now?: Date;
}) {
  const now = args.now ?? new Date();
  const payrollSettings = args.payrollSettings ?? DEFAULT_PAYROLL_SETTINGS;
  const [settings, setSettings] = useState<ObligationSettings>(DEFAULT_OBLIGATION_SETTINGS);
  const [bonusByYear, setBonusByYear] = useState<Record<number, BonusPlan>>({});
  const [taxByYear, setTaxByYear] = useState<Record<number, TaxPlan>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeObligationSettings(setSettings, (err) => setError(err.message));
  }, []);

  const yearKey = args.years.join(',');

  useEffect(() => {
    const unsubs = args.years.map((year) =>
      subscribeBonusPlan(year, settings, (plan) => {
        setBonusByYear((prev) => (prev[year] === plan ? prev : { ...prev, [year]: plan }));
      }, (err) => setError(err.message)),
    );
    return () => unsubs.forEach((u) => u());
    // settings identity is enough; yearKey tracks the year list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearKey, settings]);

  useEffect(() => {
    const unsubs = args.years.map((year) =>
      subscribeTaxPlan(year, settings, (plan) => {
        setTaxByYear((prev) => (prev[year] === plan ? prev : { ...prev, [year]: plan }));
      }, (err) => setError(err.message)),
    );
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearKey, settings]);

  const locationByUserId = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const u of args.crmUsers) m.set(u.uid, u.location ?? null);
    return m;
  }, [args.crmUsers]);

  const bonusFor = useCallback(
    (year: number, override?: BonusPlan): BonusPlan =>
      override ?? bonusByYear[year] ?? createDefaultBonusPlan(year, settings),
    [bonusByYear, settings],
  );

  const taxFor = useCallback(
    (year: number, override?: TaxPlan): TaxPlan =>
      override ?? taxByYear[year] ?? createDefaultTaxPlan(year, settings),
    [taxByYear, settings],
  );

  const computedBonus = useCallback(
    (year: number, override?: BonusPlan, taxOverride?: TaxPlan): ComputedBonusPlan =>
      computeBonusPlan(bonusFor(year, override), args.positions, {
        payrollSettings,
        locationByUserId,
        now,
        estimatedProfit: taxFor(year, taxOverride).estimatedAnnualProfit,
      }),
    [args.positions, bonusFor, locationByUserId, now, payrollSettings, taxFor],
  );

  const computedTax = useCallback(
    (year: number, override?: TaxPlan, bonusOverride?: BonusPlan): ComputedTaxPlan => {
      const bonus = computedBonus(year, bonusOverride, override);
      return computeTaxPlan(taxFor(year, override), args.positions, {
        fiscalYearEndMonth: settings.fiscalYearEndMonth,
        now,
        bonusDeduction: bonus.enabled ? bonus.cashOutTotal : 0,
      });
    },
    [args.positions, computedBonus, now, settings.fiscalYearEndMonth, taxFor],
  );

  const slicesFor = useCallback(
    (opts?: { bonusDraft?: BonusPlan | null; taxDraft?: TaxPlan | null }): ObligationSlice[] => {
      return args.years.flatMap((year) => {
        const taxOverride = opts?.taxDraft && opts.taxDraft.year === year ? opts.taxDraft : undefined;
        const bonusOverride = opts?.bonusDraft && opts.bonusDraft.year === year ? opts.bonusDraft : undefined;
        const bonus = computedBonus(year, bonusOverride, taxOverride);
        const tax = computedTax(year, taxOverride, bonusOverride);
        return [...bonus.slices, ...tax.slices];
      });
    },
    [args.years, computedBonus, computedTax],
  );

  const slices = useMemo(() => slicesFor(), [slicesFor]);

  const persistSettings = useCallback(async (next: ObligationSettings) => {
    if (!args.actorUserId) return;
    setBusy(true);
    setError(null);
    try {
      await saveObligationSettings(next, args.actorUserId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save obligation settings.');
    } finally {
      setBusy(false);
    }
  }, [args.actorUserId]);

  const persistBonus = useCallback(async (plan: BonusPlan) => {
    if (!args.actorUserId) return;
    setBusy(true);
    setError(null);
    try {
      await saveBonusPlan(plan, args.actorUserId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save bonus plan.');
    } finally {
      setBusy(false);
    }
  }, [args.actorUserId]);

  const persistTax = useCallback(async (plan: TaxPlan) => {
    if (!args.actorUserId) return;
    setBusy(true);
    setError(null);
    try {
      await saveTaxPlan(plan, args.actorUserId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save tax plan.');
    } finally {
      setBusy(false);
    }
  }, [args.actorUserId]);

  return {
    settings,
    bonusByYear,
    taxByYear,
    bonusFor,
    taxFor,
    computedBonus,
    computedTax,
    slices,
    slicesFor,
    busy,
    error,
    persistSettings,
    persistBonus,
    persistTax,
  };
}
